/**
 * Match QBO payment invoice links to hub GOSI rows.
 *
 * Canonical model (hub ↔ QuickBooks Online):
 *
 * 1. **Display / API `invoice_number`** — `public.invoice.number` (numeric only, e.g. `168724`).
 *    Never prefer `qb_doc_number` for display; strip GOSI only when `number` is empty.
 *
 * 2. **PRIMARY payment link** — `public.invoice.qb_id` (QBO Invoice Id) after backfill.
 *    Payment sync matches allocations by `qb_id` first.
 *
 * 3. **Backfill when `qb_id` is null** — read-only QBO invoice list; match
 *    `DocNumber = hubNumberToQboDocNumber(number)` (e.g. `GOSI 168724`).
 *    Tool: `npm run tool -- backfill-qbo-invoice-links`.
 *
 * 4. **Doc-number fallback** (edge cases only) — same GOSI normalization via
 *    {@link canonicalGosiSalesDocKey} / {@link hubNumberToQboDocNumber}.
 *
 * Helpers: {@link hubNumberToQboDocNumber} / {@link qboDocNumberToHubNumber} /
 * {@link hubInvoiceNumberForDisplay}.
 */

import {
  toCanonicalSalesDocNumber,
  toKey,
} from "../accounting/qbo-doc-number.js";

export type InvoiceLinkMatchPath = "qb_id" | "doc_number";

/** Hub numeric invoice number → QBO sales invoice DocNumber (`GOSI {n}`). */
export function hubNumberToQboDocNumber(hubNumber: string): string {
  const trimmed = hubNumber.trim();
  const canonical = toCanonicalSalesDocNumber(trimmed);
  if (canonical != null) {
    return canonical;
  }
  return `GOSI ${trimmed}`;
}

/** QBO sales invoice DocNumber → hub numeric `public.invoice.number`. */
export function qboDocNumberToHubNumber(docNumber: string): string | null {
  const canonical = toCanonicalSalesDocNumber(docNumber);
  if (canonical != null) {
    return canonical.slice("GOSI ".length);
  }
  const trimmed = docNumber.trim();
  if (/^\d+$/.test(trimmed)) {
    return trimmed;
  }
  return null;
}

/** Display-only: numeric hub invoice number (strips GOSI prefix from QBO DocNumber). */
export function hubInvoiceNumberForDisplay(docNumber: string | null | undefined): string {
  if (docNumber == null) {
    return "";
  }
  const trimmed = docNumber.trim();
  if (trimmed === "") {
    return "";
  }
  return qboDocNumberToHubNumber(trimmed) ?? trimmed;
}

/** Normalize GOSI invoice / QBO DocNumber to a comparable key (e.g. "GOSI 168984"). */
export function canonicalGosiSalesDocKey(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  return toKey(hubNumberToQboDocNumber(trimmed));
}

/** Whether hub `public.invoice.number` matches a QBO invoice DocNumber after GOSI normalization. */
export function gosiDocNumbersMatch(invoiceNumber: string, qboDocNumber: string): boolean {
  const invoiceKey = canonicalGosiSalesDocKey(invoiceNumber);
  const qboKey = canonicalGosiSalesDocKey(qboDocNumber);
  return invoiceKey != null && qboKey != null && invoiceKey === qboKey;
}

export interface InvoiceLookupMaps {
  byQbId: Map<string, number>;
  byDocKey: Map<string, number>;
}

export function buildInvoiceLookupMaps(
  rows: Array<{ mv_id: number | string; qb_id?: string | null; number?: string | null }>,
): InvoiceLookupMaps {
  const byQbId = new Map<string, number>();
  const byDocKey = new Map<string, number>();
  for (const row of rows) {
    const mvId = Number(row.mv_id);
    if (!Number.isFinite(mvId)) {
      continue;
    }
    const qbId = row.qb_id != null ? String(row.qb_id).trim() : "";
    if (qbId !== "") {
      byQbId.set(qbId, mvId);
    }
    const number = row.number != null ? String(row.number) : "";
    const key = canonicalGosiSalesDocKey(number);
    if (key != null && !byDocKey.has(key)) {
      byDocKey.set(key, mvId);
    }
  }
  return { byQbId, byDocKey };
}

export function resolveInvoiceMvIdForPayment(input: {
  qbInvoiceId: string;
  invoiceMaps: InvoiceLookupMaps;
  qboInvoiceDocById: Map<string, string>;
}): { invoiceMvId: number | null; matchPath: InvoiceLinkMatchPath | null } {
  const qbInvoiceId = input.qbInvoiceId.trim();
  if (qbInvoiceId === "") {
    return { invoiceMvId: null, matchPath: null };
  }

  const byQbId = input.invoiceMaps.byQbId.get(qbInvoiceId);
  if (byQbId != null) {
    return { invoiceMvId: byQbId, matchPath: "qb_id" };
  }

  const qboDocNumber = input.qboInvoiceDocById.get(qbInvoiceId);
  if (qboDocNumber != null) {
    const docKey = canonicalGosiSalesDocKey(qboDocNumber);
    if (docKey != null) {
      const byDocNumber = input.invoiceMaps.byDocKey.get(docKey);
      if (byDocNumber != null) {
        return { invoiceMvId: byDocNumber, matchPath: "doc_number" };
      }
    }
  }

  return { invoiceMvId: null, matchPath: null };
}
